import React, { Fragment, memo } from 'react';
import { arrayOf, string, bool, object, array } from 'prop-types';
import { isEmpty } from 'ramda';
// Components
import Segment from '../../../../Segment';
import SegmentRating from '../../../../Segment/SegmentRating';
import SegmentOptions from '../../../../Segment/SegmentOptions';
import Icon from '../../../../../directives/Icon/Icon';
// helpers
import { getCriticalText } from '../../../../../../helpers/new-trip-details-helper';
import { getTrueOptions } from '../../../../../../helpers/account-helper';
// Layouts
import { DefaultLayout, DefaultMobileLayout } from './Layouts';
// Styles
import * as Styled from './Style';
const RouteInfo = ({
  route,
  date,
  time,
  frequency,
  regularity
}) => {
  const routeDate = I18n.l('date.formats.details_date', date);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: route
    }
  }), date && /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: I18n.t('pages.search.trip.tabs.route.route_date', {
        date: routeDate
      })
    }
  }), time && /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: I18n.t('pages.search.trip.tabs.route.route_time', {
        time
      })
    }
  })), frequency && /*#__PURE__*/React.createElement("p", null, I18n.t('pages.search.trip.tabs.route.frequency_title'), frequency), regularity && /*#__PURE__*/React.createElement("p", null, I18n.t('pages.search.trip.tabs.route.regularity_title'), I18n.t(`pages.search.trip.tabs.route.regularity.${regularity}`)), /*#__PURE__*/React.createElement("p", null, I18n.t('components.Order.local_time')));
};
const RouteOptions = ({
  options = [],
  isEticket = false
}) => {
  if (isEmpty(options)) return null;
  const renderedOptions = isEticket ? options.filter(option => option !== 'require-printed-ticket') : options;
  const optionsWithIcons = getTrueOptions(renderedOptions);
  return /*#__PURE__*/React.createElement(SegmentOptions, {
    options: optionsWithIcons,
    isIconShow: true
  });
};
const RouteRating = ({
  rating = {}
}) => {
  if (isEmpty(rating)) return null;
  return /*#__PURE__*/React.createElement(SegmentRating, {
    rating: rating
  });
};
const Title = ({
  startCity,
  endCity,
  text,
  isCheckout = false
}) => {
  if (!isCheckout) return null;
  const title = `${startCity} – ${endCity}`;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Styled.SubTitle, null, text), /*#__PURE__*/React.createElement(Styled.Title, null, title));
};
const TripInfo = ({
  layout: Layout,
  mobileLayout: MobileLayout,
  segments,
  criticalInfo = [],
  isMobile,
  transferTime,
  refundPolicyUrl,
  timeInRoad,
  isEticket,
  isCheckout,
  isDashed,
  isPreviewDetails
}) => {
  const getSegment = () => {
    const hasManySegments = segments.length > 1;
    return segments.map((segment, index) => {
      const route = I18n.t('pages.search.trip.tabs.route.route_description', {
        number: segment.number,
        startCityName: segment.startCity,
        endCityName: segment.endCity
      });
      const isOptionsHidden = !(isCheckout && isMobile);
      const isCarrierHidden = !isCheckout && isMobile;
      return (
        // eslint-disable-next-line react/no-array-index-key
        /*#__PURE__*/
        React.createElement(Fragment, {
          key: `trip-segment-${index}`
        }, !isMobile &&
        // eslint-disable-next-line react/no-array-index-key
        /*#__PURE__*/
        React.createElement(Styled.DetailsRoute, {
          key: `trip-details__route-${index}`
        }, /*#__PURE__*/React.createElement(Styled.DetailsRouteColumn, null, /*#__PURE__*/React.createElement(Styled.DetailsRouteInfo, null, /*#__PURE__*/React.createElement(RouteInfo, {
          route: route,
          date: segment.startDate,
          time: segment.startTime,
          frequency: segment.frequency,
          regularity: segment.regularity
        }))), /*#__PURE__*/React.createElement(Styled.DetailsRouteColumn, null, /*#__PURE__*/React.createElement(RouteOptions, {
          options: segment.options,
          isEticket: isEticket
        }), /*#__PURE__*/React.createElement(RouteRating, {
          rating: segment.rating
        }))), /*#__PURE__*/React.createElement(Segment, {
          // eslint-disable-next-line react/no-array-index-key
          key: `trip-details__segment-${index}`,
          ...segment,
          hasManySegments: hasManySegments,
          isRatingHidden: true,
          isOptionsHidden: isOptionsHidden,
          isCarrierHidden: isCarrierHidden,
          isShowSegmentDevider: hasManySegments && index === 0,
          transferTime: transferTime,
          isCriticalInfoShown: false,
          refundPolicyUrl: refundPolicyUrl,
          isDashed: isDashed,
          isEticket: isEticket,
          isPreviewDetails: isPreviewDetails
        }))
      );
    });
  };
  const getCriticalInfo = () => {
    if (isEmpty(criticalInfo)) return null;
    return /*#__PURE__*/React.createElement(Styled.DetailsCriticalInfoWrapper, null, /*#__PURE__*/React.createElement(Styled.DetailsCriticalInfo, null, /*#__PURE__*/React.createElement(Styled.DetailsCriticalInfoIcon, null, /*#__PURE__*/React.createElement(Icon, {
      name: "info"
    })), /*#__PURE__*/React.createElement(Styled.DetailsCriticalInfoText, null, /*#__PURE__*/React.createElement("div", {
      dangerouslySetInnerHTML: {
        __html: getCriticalText(criticalInfo)
      }
    }))));
  };
  const props = {
    segment: getSegment(),
    criticalInfo: getCriticalInfo(),
    timeInRoad,
    isCheckout
  };
  const Component = () => {
    if (isMobile) return /*#__PURE__*/React.createElement(MobileLayout, props);
    return /*#__PURE__*/React.createElement(Layout, props);
  };
  return /*#__PURE__*/React.createElement(Component, null);
};
RouteInfo.propTypes = {
  route: string,
  date: string,
  time: string,
  frequency: string,
  regularity: string
};
RouteOptions.propTypes = {
  options: arrayOf(string),
  isEticket: bool
};
RouteRating.propTypes = {
  rating: object
};
Title.propTypes = {
  startCity: string,
  endCity: string,
  text: string,
  isCheckout: bool
};
TripInfo.defaultProps = {
  layout: DefaultLayout,
  mobileLayout: DefaultMobileLayout
};
TripInfo.propTypes = {
  layout: object,
  mobileLayout: object,
  segments: array.isRequired,
  transferTime: string,
  refundPolicyUrl: string,
  timeInRoad: string,
  criticalInfo: array,
  isMobile: bool,
  isEticket: bool,
  isCheckout: bool,
  isPreviewDetails: bool,
  isDashed: bool
};
export default /*#__PURE__*/memo(TripInfo);